import { useState, useRef, useCallback } from "react";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";

interface UseSummaryGeneratorProps {
  summaryServiceUrl: string;
  currentMeetingId: string | null;
  targetLang: string;
  targetLangLabel: string;
  meetingTranscript: { timestamp: string; orig: string; tran: string }[];
}

export function useSummaryGenerator({
  summaryServiceUrl,
  currentMeetingId,
  targetLang,
  targetLangLabel,
  meetingTranscript,
}: UseSummaryGeneratorProps) {
  const [summaryText, setSummaryText] = useState("");
  const [s3Url, setS3Url] = useState("");
  const [htmlFilename, setHtmlFilename] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPolling, setIsPolling] = useState(false);

  const fetchInProgress = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetGeneration = useCallback(() => {
    setSummaryText("");
    setS3Url("");
    setHtmlFilename("");
    setError("");
    setIsPolling(false);
    fetchInProgress.current = false;
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
  }, []);

  const setSavedSummary = useCallback((text: string) => {
    setSummaryText(text);
    setLoading(false);
  }, []);

  const compileTranscriptText = () => {
    return meetingTranscript
      .map((t) => `[${t.timestamp}] Origen: "${t.orig}" | Traducción: "${t.tran}"`)
      .join("\n");
  };

  const pollStatus = async (meetingId: string | number) => {
    setIsPolling(true);
    setLoading(true);
    setError("");

    pollIntervalRef.current = setInterval(async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const res = await fetch(`${summaryServiceUrl}/summarize/${meetingId}/status`, {
          headers: authHeaders,
        });
        if (!res.ok) return;

        const data = await res.json();

        if (data.status === "completed") {
          clearInterval(pollIntervalRef.current!);
          if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
          setSummaryText(data.summary || "No se recibió respuesta del resumen.");
          setS3Url(data.s3_url || "");
          setHtmlFilename(data.html_filename || "");
          setIsPolling(false);
          setLoading(false);
        } else if (data.status === "failed") {
          clearInterval(pollIntervalRef.current!);
          if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
          setError(data.error || "Fallo interno al generar la minuta.");
          setIsPolling(false);
          setLoading(false);
        }
      } catch (err) {
        console.warn("Error consultando el status de la minuta", err);
      }
    }, 5000);

    safetyTimeoutRef.current = setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        setError("Tiempo de espera agotado. La minuta tardó más de 15 minutos.");
        setIsPolling(false);
        setLoading(false);
      }
    }, 15 * 60 * 1000);
  };

  const generateSummary = async () => {
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;
    setLoading(true);
    setError("");

    const transcriptText = compileTranscriptText();

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${summaryServiceUrl}/summarize`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          transcript: transcriptText,
          meeting_id: currentMeetingId,
          format_type: `minuta de reunión en ${targetLangLabel}`,
          target_lang: targetLang,
        }),
      });

      if (!response.ok) {
        if (response.status === 504 || response.status === 503 || response.status === 502) {
          if (currentMeetingId) pollStatus(currentMeetingId);
          return;
        }

        let errorMsg = `Error del servidor: ${response.status} ${response.statusText}`;
        try {
          const errData = await response.json();
          if (errData.code === "TIMEOUT") errorMsg = "El servidor tardó demasiado";
          else if (errData.code === "EMPTY_TRANSCRIPT") errorMsg = "No se capturaron suficientes segmentos";
          else if (errData.code === "MODEL_UNAVAILABLE") errorMsg = "El servicio de IA no está disponible";
          else if (errData.error) errorMsg = errData.error;
        } catch (e) {
          // Ignorar si no es JSON
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();

      if (!data.summary && currentMeetingId) {
        pollStatus(currentMeetingId);
        return;
      }

      setSummaryText(data.summary || "No se recibió respuesta del resumen.");
      setS3Url(data.s3_url || "");
      setHtmlFilename(data.html_filename || "");
    } catch (err: any) {
      if (err.message?.includes("fetch") || err.message?.includes("Network") || err.message?.includes("Failed")) {
        if (currentMeetingId) {
          pollStatus(currentMeetingId);
          return;
        }
      }
      console.error(err);
      setError(err.message || "Error al conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  return {
    summaryText,
    s3Url,
    htmlFilename,
    loading,
    error,
    isPolling,
    generateSummary,
    resetGeneration,
    setSavedSummary,
    setError,
  };
}
